import { Link } from "react-router-dom";
import { Avatar, Card, Tag, Typography } from "@/components/ui/atoms";
import { Skeleton } from "@/components/ui/atoms/Skeleton";
import type { BlogPostViewModel } from "../../types";
import { formatLongDate } from "@/utils/date";
import { getBlogPrimaryImage } from "../../utils";

const cardClassName =
  "group flex h-full flex-col overflow-hidden rounded-3xl border-dark-invirtus-soft transition-[box-shadow,border-color,transform] duration-200 hover:border-dark-invirtus/15 hover:shadow-md";

const postCardImageWrapperClassName = "aspect-[16/10] w-full shrink-0 bg-light-lila";

type PostCardProps =
  | { isLoading: true; post?: never }
  | { isLoading?: false; post: BlogPostViewModel };

export function PostCard(props: PostCardProps) {
  if (props.isLoading) {
    return (
      <Card variant="outlined" padding="none" rounded="xl" className={cardClassName}>
        <div className="flex flex-1 flex-col p-5 md:p-6">
          <div className="flex items-center justify-between gap-3">
            <Skeleton decorative className="h-5 w-20 rounded-full" />
            <Skeleton decorative className="h-3 w-24" />
          </div>
          <Skeleton.Text decorative lines={2} className="mt-4 [&_div]:h-5" />
          <Skeleton.Text decorative lines={3} className="mt-3 flex-1" />
          <div className="mt-5 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Skeleton decorative className="h-6 w-6 rounded-full" />
              <Skeleton decorative className="h-4 w-24" />
            </div>
            <Skeleton decorative className="h-4 w-20" />
          </div>
        </div>
      </Card>
    );
  }

  const { post } = props;
  const imageSrc = getBlogPrimaryImage(post);

  return (
    <Card variant="outlined" padding="none" rounded="xl" className={cardClassName}>
      {imageSrc ? (
        <Link
          to={`/blog/${post.slug}`}
          className="block shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-dark-invirtus/20"
        >
          <div className={postCardImageWrapperClassName}>
            <img
              src={imageSrc}
              alt={post.coverImageAlt || post.title}
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
              loading="lazy"
            />
          </div>
        </Link>
      ) : null}

      <div className="flex flex-1 flex-col p-5 md:p-6">
        <div className="flex items-center justify-between">
          {!!post.tags?.length && (
            <div>
              <Tag variant="subtle" intent="primary" palette="brand">
                {post.tags[0].name}
              </Tag>
            </div>
          )}
          <Typography as="p" variant="body-xs" color="muted">
            {formatLongDate(post.publishedAt || post.createdAt)}
          </Typography>
        </div>

        <Typography as="h2" variant="h5" className="mt-4 line-clamp-2 leading-snug">
          <Link
            to={`/blog/${post.slug}`}
            className="text-dark-invirtus transition-colors hover:text-dark-invirtus/80 focus:outline-none focus-visible:underline"
          >
            {post.title}
          </Link>
        </Typography>

        <Typography as="p" variant="body-sm" className="mt-3 line-clamp-3 flex-1 text-dark-invirtus/65">
          {post.excerpt}
        </Typography>

        <div className="mt-5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Avatar
              initials={post.author?.name || "Invirtus"}
              src={post.author?.avatarUrl}
              alt={post.author?.name || "Invirtus"}
              size="sm"
              variant={post.author?.avatarUrl ? "neutral" : "gradient"}
              className="max-w-6 max-h-6"
            />
            <Typography as="p" variant="body-sm" weight="semibold" color="primary">
              {post.author?.name || "Invirtus"}
            </Typography>
          </div>
          <Link
            to={`/blog/${post.slug}`}
            className="inline-flex w-fit text-sm font-semibold text-purple underline-offset-4 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-dark-invirtus/20"
          >
            Read more
          </Link>
        </div>
      </div>
    </Card>
  );
}
